import { Component, EventEmitter, Output, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Clinician } from 'app/views/orders/orders-table/orders-table.component';
import { INTERVAL_DEFAULT, resolveIntervalDays } from 'app/views/patient-programs/follow-up-interval-picker.component';

export interface EscalationAnswers {
  escalation_reason?: string;
  general_reason_details?: string;
  patient_info_required?: string;
  patient_consulted?: string;
  patient_consulted_general?: string;
  patient_consulted_confirm?: boolean;
}

type AnswerValue = string | number | boolean | string[];

export interface QuestionOption {
  value: string;
  label: string;
  nextQuestion?: string;
}

export interface Question {
  id: string;
  type: 'dropdown' | 'textarea' | 'multi-select' | 'clinician-select' | 'number' | 'confirm' | 'follow-up-interval';
  label: string;
  placeholder?: string;
  required?: boolean;
  note?: string;
  options?: QuestionOption[];
  clinicians?: Clinician[];
  allClinicians?: Clinician[];
  min?: number;
  max?: number;
  showWhen?: (answers: Record<string, any>) => boolean;
  validation?: (value: AnswerValue) => boolean;
  messageOnAnswer?: (value: AnswerValue) => string | null;
  // Lets this question's answer set another question's answer (e.g. picking
  // a dose prefills a cadence field with that dose's default) - returns
  // { questionId, value } or null for no side effect.
  onAnswerSideEffect?: (value: AnswerValue) => { questionId: string; value: AnswerValue } | null;
  // For 'follow-up-interval': the default option and preview dates, from the current answers.
  intervalContext?: (answers: Record<string, any>) => FollowUpIntervalContext;
}

export interface FollowUpIntervalContext {
  defaultDays: number | null;
  defaultLabelPrefix: string;
  cycleStart: Date | null;
  leadDays: number | null;
}

export interface DialogConfig {
  title: string;
  questions: Question[];
  tags?: { id: number; name: string }[];
  labelMap?: { [value: string]: string };
  prefillAnswers?: Partial<EscalationAnswers>;
}

interface Answer {
  questionId: string;
  value: AnswerValue;
}

@Component({
    selector: 'app-dialog-out-of-scope',
    templateUrl: './dialog-out-of-scope.component.html',
    styleUrls: ['./dialog-out-of-scope.component.scss'],
    standalone: false
})
export class DialogOutOfScopeComponent {

  answers: Map<string, AnswerValue> = new Map();
  visibleQuestions: Question[] = [];
  selectFromAllClinicians: boolean = false;

  // Alert box properties
  alertMessage: string = '';
  alertType: 'info' | 'warn' | 'success' | 'error' = 'info';
  isAlertVisible: boolean = false;

  @Output() dataSelected = new EventEmitter<EscalationAnswers>();

  constructor(
    public dialogRef: MatDialogRef<DialogOutOfScopeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogConfig
  ) {
    if (data?.prefillAnswers) {
      Object.entries(data.prefillAnswers).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          this.answers.set(key, value as AnswerValue);
        }
      });
    }
    (data?.questions || [])
      .filter(q => q.type === 'follow-up-interval' && !this.answers.has(q.id))
      .forEach(q => this.answers.set(q.id, INTERVAL_DEFAULT));
    this.updateVisibleQuestions();
  }

  private updateVisibleQuestions() {
    const answersObj = this.getAnswersAsObject();

    this.visibleQuestions = (this.data.questions || []).filter(question => {
      if (!question.showWhen) return true;
      return question.showWhen(answersObj);
    });
  }

  onAnswerChange(questionId: string, value: AnswerValue) {
    this.answers.set(questionId, value);
    if (questionId === 'escalation_reason') {
      const escalationReason = value;
      this.answers.clear();
      this.isAlertVisible = false;
      this.alertMessage = '';
      this.answers.set('escalation_reason', escalationReason);
    }

    // Check if this answer triggers a message
    const question = this.data.questions.find(q => q.id === questionId);
    if (question?.messageOnAnswer) {
      const message = question.messageOnAnswer(value);
      if (message) {
        this.showAlert(message, 'info');
      }
    }

    if (question?.onAnswerSideEffect) {
      const sideEffect = question.onAnswerSideEffect(value);
      if (sideEffect) {
        this.answers.set(sideEffect.questionId, sideEffect.value);
      }
    }

    // Update visible questions based on new answers
    this.updateVisibleQuestions();
  }

  getIntervalContext(question: Question): FollowUpIntervalContext | null {
    return question.intervalContext ? question.intervalContext(this.getAnswersAsObject()) : null;
  }

  getAnswer(questionId: string): AnswerValue | undefined {
    return this.answers.get(questionId);
  }

  private getAnswersAsObject(): Record<string, any> {
    const obj: Record<string, any> = {};
    this.answers.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  canSubmit(): boolean {
    // Check all visible required questions have answers
    for (const question of this.visibleQuestions) {
      if (question.required) {
        const answer = this.answers.get(question.id);

        // Check if answer exists and passes validation
        if (answer === undefined || answer === null || answer === '') return false;

        if (
          question.type === 'textarea' &&
          (typeof answer !== 'string' || answer.trim() === '')
        ) {
          return false;
        }

        if (question.type === 'confirm' && answer !== true) {
          return false;
        }

        if (question.type === 'follow-up-interval' && answer !== INTERVAL_DEFAULT && resolveIntervalDays(answer, null) === null) {
          return false;
        }

        if (question.validation && !question.validation(answer)) {
          return false;
        }
      }
    }

    return this.answers.size > 0;
  }

  closeDialog() {
    const result = this.getAnswersAsObject() as EscalationAnswers;
    this.dataSelected.emit(result);
    this.dialogRef.close(result);
  }

  // Utility methods for template
  isQuestionVisible(question: Question): boolean {
    return this.visibleQuestions.includes(question);
  }

  getDropdownOptions(question: Question): QuestionOption[] {
    return question.options || [];
  }

  getDisplayLabel(question: Question, optionValue: string): string {
    if (this.data.labelMap && this.data.labelMap[optionValue]) {
      return this.data.labelMap[optionValue];
    }

    const option = question.options?.find(opt => opt.value === optionValue);
    return option?.label || optionValue;
  }

  trackByQuestionId(index: number, question: Question): string {
    return question.id;
  }

  onMultiSelectChange(questionId: string, selectedTags: { name: string; id: number }[]) {
    const value = selectedTags.map(tag => tag.name).join(',');
    this.onAnswerChange(questionId, value);
  }

  onClinicianSelectChange(questionId: string, selected: Clinician | { isUnassign: true }) {
    if ('id' in selected) {
      this.onAnswerChange(questionId, selected.id);
    }
  }

  getClinicianOptions(question: Question): Clinician[] {
    if (this.selectFromAllClinicians && question.allClinicians?.length) {
      return question.allClinicians;
    }
    return question.clinicians || [];
  }

  showAlert(message: string, type: 'info' | 'warn' | 'success' | 'error' = 'info') {
    this.alertMessage = message;
    this.alertType = type;
    this.isAlertVisible = true;
  }

  onAlertDismiss() {
    this.isAlertVisible = false;
    this.alertMessage = '';
  }

  getOptionId(questionId: string, value: string): string {
    return 'option-' + questionId + '-' + value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  onDropdownOpenedChange(questionId: string, opened: boolean) {
    if (questionId === 'escalation_reason' && opened) {
      window.dispatchEvent(new CustomEvent('ft:escalation-dropdown-opened'));
    }
    if (questionId === 'escalation_reason' && !opened && !this.answers.get('escalation_reason')) {
      window.dispatchEvent(new CustomEvent('ft:escalation-cancelled'));
    }
  }
}



